import React, { useState, useEffect } from 'react';
import { Loader2, Save, Receipt } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';

const fmt = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

function Field({ label, hint, value, onChange, disabled }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>
      <Input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`h-8 text-sm text-right ${disabled ? 'bg-muted/50 text-muted-foreground' : ''}`}
        data-role="mono"
      />
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

function Quad({ title, children, subtotalLabel, subtotalValue }) {
  return (
    <div className="border rounded-lg p-4 space-y-3 flex flex-col">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-foreground">{title}</h3>
      <div className="space-y-3 flex-1">{children}</div>
      <div className="flex justify-between items-center text-xs bg-muted/50 rounded px-2.5 py-1.5 mt-auto">
        <span className="font-medium text-muted-foreground">{subtotalLabel}</span>
        <span className="font-semibold" data-role="mono">{fmt(subtotalValue)}</span>
      </div>
    </div>
  );
}

export default function ExpenseLedgerModal({ open, onOpenChange, project, onSave }) {
  const [eb, setEb] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && project) {
      const raw = project.expense_breakdown || {};
      setEb({
        labor_cost: raw.labor_cost ?? project.project_cost ?? 0,
        labor_adjustment: raw.labor_adjustment ?? 0,
        tool_costs: raw.tool_costs ?? 0,
        misc_costs: raw.misc_costs ?? raw.other_costs ?? 0,
        monthly_recurring: raw.monthly_recurring ?? 0,
        annual_recurring: raw.annual_recurring ?? 168,
      });
    }
  }, [open, project]);

  const laborTotal = (parseFloat(eb.labor_cost) || 0) + (parseFloat(eb.labor_adjustment) || 0);
  const monthlyAnnualized = (parseFloat(eb.monthly_recurring) || 0) * 12;
  const annualAmount = parseFloat(eb.annual_recurring) || 0;
  const recurringTotal = monthlyAnnualized + annualAmount;
  const oneTimeTotal = parseFloat(eb.tool_costs) || 0;
  const miscTotal = parseFloat(eb.misc_costs) || 0;
  const grandTotal = laborTotal + recurringTotal + oneTimeTotal + miscTotal;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(project.id, {
        labor_cost: parseFloat(eb.labor_cost) || 0,
        labor_adjustment: parseFloat(eb.labor_adjustment) || 0,
        tool_costs: parseFloat(eb.tool_costs) || 0,
        misc_costs: parseFloat(eb.misc_costs) || 0,
        monthly_recurring: parseFloat(eb.monthly_recurring) || 0,
        annual_recurring: parseFloat(eb.annual_recurring) || 0,
      });
      onOpenChange(false);
    } catch {} finally { setSaving(false); }
  };

  const update = (field, val) => setEb(prev => ({ ...prev, [field]: val }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 flex flex-col" style={{ maxHeight: '80vh' }}>
        <DialogHeader className="px-6 pt-5 pb-3 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2" data-role="heading">
            <Receipt className="h-5 w-5 text-primary" />
            Project Expense Ledger
          </DialogTitle>
          <p className="text-xs text-muted-foreground">{project?.project_name}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Top-Left: LABOR */}
            <Quad title="Labor" subtotalLabel="Labor Subtotal" subtotalValue={laborTotal}>
              <Field
                label="Internal Labor (ClickUp)"
                value={eb.labor_cost}
                onChange={(v) => update('labor_cost', v)}
                disabled
                hint="Auto-calculated from tracked time"
              />
              <Field
                label="Manual Labor Adj. ($)"
                value={eb.labor_adjustment}
                onChange={(v) => update('labor_adjustment', v)}
                hint="Untracked labor or contractor costs"
              />
            </Quad>

            {/* Top-Right: ANNUALIZED RECURRING */}
            <Quad title="Annualized Recurring" subtotalLabel="Recurring Subtotal" subtotalValue={recurringTotal}>
              <Field
                label="Monthly Recurring"
                value={eb.monthly_recurring}
                onChange={(v) => update('monthly_recurring', v)}
                hint={`${fmt(eb.monthly_recurring)}/mo x 12 = ${fmt(monthlyAnnualized)}`}
              />
              <Field
                label="Annual Recurring"
                value={eb.annual_recurring}
                onChange={(v) => update('annual_recurring', v)}
                hint={`${fmt(eb.annual_recurring)}/yr \u00f7 12 = ${fmt(annualAmount / 12)}/mo`}
              />
            </Quad>

            {/* Bottom-Left: ONE-TIME COSTS */}
            <Quad title="One-Time Costs" subtotalLabel="One-Time Subtotal" subtotalValue={oneTimeTotal}>
              <Field
                label="Tools / Software / Licenses"
                value={eb.tool_costs}
                onChange={(v) => update('tool_costs', v)}
                hint="Stock photos, premium plugins, etc."
              />
            </Quad>

            {/* Bottom-Right: MISCELLANEOUS */}
            <Quad title="Miscellaneous" subtotalLabel="Misc. Subtotal" subtotalValue={miscTotal}>
              <Field
                label="Other Expenses"
                value={eb.misc_costs}
                onChange={(v) => update('misc_costs', v)}
                hint="Any other project expenses"
              />
            </Quad>
          </div>
        </div>

        <div className="flex-shrink-0 border-t bg-card px-6 py-3 space-y-3">
          <div className="flex justify-between items-center bg-muted rounded-lg px-4 py-2.5">
            <div>
              <span className="text-sm font-semibold block">Total Project Cost</span>
              <span className="text-[10px] text-muted-foreground">Full first-year cost</span>
            </div>
            <span className="text-lg font-bold text-primary" data-role="mono">{fmt(grandTotal)}</span>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save Expenses
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
